// EMBED locale catalog source — what widget/build.mjs substitutes for
// ./catalogs when bundling. Only the pt (default) catalog is inlined, as the
// guaranteed offline-safe fallback; every other locale is fetched once from
// <base-url>/embed/locales/<locale>.json before React mounts (the entries
// await ensureCatalog alongside stylesheet injection). base-url is the admin
// origin the bundle itself was served from — the same origin injectStyles
// loads the CSS from, covered by the ACAO:* header on /embed/* — never the
// host page's origin. A failed/timed-out fetch resolves anyway and the widget
// renders in pt: translated-but-wrong-language beats a blank embed.
//
// Interface must stay identical to ./catalogs (the full table used by
// vitest/tsc); widget/src/messages.ts is the only importer.

import ptWidget from '../../src/messages/pt/widget.json';
import ptCommon from '../../src/messages/pt/common.json';
import ptContact from '../../src/messages/pt/contact.json';

import { DEFAULT_LOCALE, type Locale } from '../../src/i18n/config';
import { fetchWithTimeout } from './shadow';

export type Catalog = Record<string, unknown>;

const CATALOGS: Partial<Record<Locale, Catalog>> = {
  pt: { widget: ptWidget, common: ptCommon, contact: ptContact },
};

export function getCatalog(locale: Locale): Catalog {
  return CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE]!;
}

/** In-flight fetch per catalog URL (mirrors loadStylesheet's cssCache): two
 *  elements from the SAME bundle share one request; on failure the entry is
 *  dropped so a remount (e.g. the retry panel) re-attempts the download. */
const inflight = new Map<string, Promise<void>>();

export function ensureCatalog(locale: Locale, baseUrl: string): Promise<void> {
  if (CATALOGS[locale]) return Promise.resolve();
  const url = `${baseUrl.replace(/\/$/, '')}/embed/locales/${locale}.json`;
  let p = inflight.get(url);
  if (!p) {
    p = fetchWithTimeout(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((catalog: unknown) => {
        if (catalog && typeof catalog === 'object') {
          CATALOGS[locale] = catalog as Catalog;
        }
      })
      .catch((err) => {
        inflight.delete(url);
        // pt fallback beats broken: log and let the mount proceed.
        console.warn(`[clinic-widget] failed to load locale ${locale}:`, err);
      });
    inflight.set(url, p);
  }
  return p;
}
